import { css } from 'aphrodite';
import * as React from 'react';
import { Button } from '../../../../web/components-v2/Button';
import { Input } from '../../../../web/components-v2/Input';
import { composerStyles as styles } from './composerStyles';

export interface LinkPopoverState {
	x: number;
	y: number;
}

interface ComposerLinkPopoverProps {
	position: LinkPopoverState;
	initialUrl: string;
	initialView: 'edit' | 'preview';
	onApply: (url: string) => void;
	onDismiss: () => void;
	onUnlink?: () => void;
}

/** Link destination preview and editor, positioned beside the selected link or caret. */
export const ComposerLinkPopover = React.forwardRef<HTMLDivElement, ComposerLinkPopoverProps>(
	({ position, initialUrl, initialView, onApply, onDismiss, onUnlink }, ref) => {
		const [editing, setEditing] = React.useState(initialView === 'edit');
		const [url, setUrl] = React.useState(initialUrl);
		const navigableHref = /^(https?:|mailto:)/i.test(initialUrl) ? initialUrl : undefined;

		const handleKeyDown = (event: React.KeyboardEvent) => {
			if (event.key === 'Enter') {
				event.preventDefault();
				onApply(url);
			}
			if (event.key === 'Escape') onDismiss();
		};

		return (
			<div ref={ref} className={css(styles.linkPopover)} style={{ left: position.x, top: position.y }}>
				{editing ? (
					<>
						<Input
							type='text'
							styles={[styles.linkInput]}
							placeholder='Paste or type a link…'
							value={url}
							onChange={event => setUrl(event.target.value)}
							onKeyDown={handleKeyDown}
							autoFocus
						/>
						{onUnlink && (
							<Button variant='ghost' size='sm' onClick={onUnlink}>
								Unlink
							</Button>
						)}
						<Button variant='primary' size='sm' styles={[styles.linkApplyBtn]} onClick={() => onApply(url)}>
							Apply
						</Button>
					</>
				) : (
					<>
						<span className={css(styles.linkPreviewText)}>
							<span>Go to link:</span>
							{navigableHref ? (
								<a
									className={css(styles.linkPreviewUrl)}
									href={navigableHref}
									target='_blank'
									rel='noopener noreferrer'
									title={initialUrl}
								>
									{initialUrl}
								</a>
							) : (
								<span className={css(styles.linkPreviewUrl)} title={initialUrl}>
									{initialUrl}
								</span>
							)}
						</span>
						<Button variant='link' size='sm' onClick={() => setEditing(true)}>
							Change
						</Button>
						{onUnlink && (
							<Button variant='link' size='sm' onClick={onUnlink}>
								Remove
							</Button>
						)}
					</>
				)}
			</div>
		);
	}
);

ComposerLinkPopover.displayName = 'ComposerLinkPopover';
